/**
 * [LGP 2026-10-15, user] NEW FILE — Lead Generator Performance → Monthly Performance: the Apps Script page "Monthly
 * Performance" (Index.html data-page="leads-analysis"; JavaScript.html loadAnalysis, renderAnalysisHead, renderAnalysis,
 * cellsHtml, sumMonths, patchAnalysisPerson). Three months side by side ending at the picked month; per person (Active only,
 * their side in brackets) Leads Count, Sales Count, Conversion %, Lead Cost ($) and In Bank ($); team banners (owner first), a
 * Total under each team (the search's own sum while searching) and the Grand Total (always the whole set). Search by
 * "name (side)" — "row" lists the ROW people. No header sort, as in the script.
 * [LGP-SIDES 2026-10-15, user] "after search bar i want a toggle which will have overall what we are seeing right now, then us
 * then row … when i come to us then all the names for us will show with the us tag to the name and same when i go to row":
 * Overall | US | ROW after the search. Overall = the script's page as it was (names carry their (US) / (ROW) tag). US / ROW = that
 * side's people only, their team Totals and the Grand Total summed over that side (a person on both sides counts there with
 * that side's own figures — the server's sideMonths); there the names carry NO tag — user: "in the us all people is from us
 * only so why the tag … no need for tag". Each view keeps its own table and the switch slides them, like Leads Count's US | ROW.
 * [LGP-CYCLE 2026-10-27, user] "no need for apply button simply select the date and data will get change according to that": a
 * picked "Up to month" loads at once — the Apply button is gone (as on Leads Count and NAL Leads).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import GlassSelect from '../../components/GlassSelect';
import { SkeletonBox } from '../../components/Skeleton';
import { SlidePane, slidePos } from '../../components/ViewSlide';
import { fetchLgpMonthly, LgpMonthCell, LgpMonthlyReport } from '../../services/api';
import {
  ViewWrap, ViewBar, Field, Toolbar, SearchInput, StatusLine, TableScroll, SideViewport, Seg, dark,
  MONTH_NAMES, todayIst, defaultRange, monthsEndingAt, ownerFirst, fmtInt, fmtPct, fmtUsd, errorText, ViewProps, PersonPatch,
} from './lgpShared';

/** [JavaScript.html A_COLS] */
const A_COLS: { key: keyof LgpMonthCell; label: string; fmt: (v: number) => string }[] = [
  { key: 'leads', label: 'Leads Count', fmt: fmtInt },
  { key: 'sales', label: 'Sales Count', fmt: fmtInt },
  { key: 'conversion', label: 'Conversion %', fmt: fmtPct },
  { key: 'costUsd', label: 'Lead Cost', fmt: fmtUsd },
  { key: 'bank', label: 'In Bank', fmt: fmtUsd },
];

/** [LGP-SIDES] The switch: Overall (the script's page), then US, then ROW — left to right, the way the tables slide. */
type SideView = 'all' | 'us' | 'row';
const SIDE_VIEWS: readonly SideView[] = ['all', 'us', 'row'];
const SIDE_OPTIONS: { key: SideView; label: string }[] = [{ key: 'all', label: 'Overall' }, { key: 'us', label: 'US' }, { key: 'row', label: 'ROW' }];
const SIDE_TAG: Record<'us' | 'row', string> = { us: 'US', row: 'ROW' };

type MonthRow = LgpMonthlyReport['rows'][number];

/** The "Up to month" choices. [LGP-MONTHS 2026-11-02, user] "the month should be latest to old and also the latest month should be
 *  the current month, right now it is showing the month for so long which is of no need": this month first, back to January 2026
 *  (the script's floor) — no months ahead any more (was January 2026 up to a year ahead of this month, oldest first). */
function monthChoices(now: string): { iso: string; label: string }[] {
  const [y, m] = now.split('-').map(Number);
  const out: { iso: string; label: string }[] = [];
  let yy = y; let mm = m;
  while (yy * 100 + mm >= 202601) {
    out.push({ iso: `${yy}-${mm < 10 ? '0' : ''}${mm}`, label: `${MONTH_NAMES[mm - 1]} ${yy}` });
    mm--; if (mm < 1) { mm = 12; yy--; }
  }
  return out;
}

/* [LIQUID-GLASS 2026-11-02, user] the month: the glass capsule + glass list; on the white card the light theme gives the capsule a
   thin grey edge (an outline, so the open glow stays) */
const MonthSelect = styled(GlassSelect)`
  ${(p) => (dark(p) ? '' : '&& { outline: 1px solid rgba(22,25,23,0.13); outline-offset: -1px; }')}
`;
const Table = styled.table`
  border-collapse: separate;
  border-spacing: 0;
  min-width: 100%;
  font-size: 13px;
  font-variant-numeric: tabular-nums;

  thead { position: sticky; top: 0; z-index: 3; }
  th {
    padding: 8px 10px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    color: ${(p) => p.theme.greenInk};
    background: ${(p) => (dark(p) ? '#262827' : '#eef0ef')};
    border-bottom: 1px solid ${(p) => p.theme.line};
    white-space: nowrap;
  }
  th.month { text-align: center; font-size: 12px; }
  th.alt, td.alt { background-clip: padding-box; }
  th.alt { background: ${(p) => (dark(p) ? '#2d302e' : '#e5e9e7')}; }
  th.first, td.first { border-left: 1px solid ${(p) => p.theme.line}; }
  td {
    padding: 8px 10px;
    border-bottom: 1px solid ${(p) => p.theme.line};
    color: ${(p) => p.theme.text};
    white-space: nowrap;
  }
  td.num, th.num { text-align: right; }
  td.alt { background: ${(p) => (dark(p) ? 'rgba(255,255,255,0.025)' : 'rgba(22,25,23,0.025)')}; }
  td.bank { color: ${(p) => p.theme.greenInk}; font-weight: 700; }
  td.hit { font-weight: 700; }
  .sticky-col { position: sticky; left: 0; z-index: 1; background: ${(p) => p.theme.surface}; text-align: left; }
  thead .sticky-col { z-index: 4; background: ${(p) => (dark(p) ? '#262827' : '#eef0ef')}; }
  tr.group td { background: ${(p) => (dark(p) ? 'rgba(255,255,255,0.055)' : 'rgba(22,25,23,0.05)')}; font-weight: 800; }
  tr.group td .count { font-weight: 600; color: ${(p) => p.theme.dim}; }
  tr.subtotal td { font-weight: 800; background: ${(p) => (dark(p) ? 'rgba(255,255,255,0.03)' : 'rgba(22,25,23,0.025)')}; }
  tr.subtotal td.sticky-col { background: ${(p) => (dark(p) ? '#232524' : '#f6f7f7')}; }
  tr.total td { font-weight: 800; background: ${(p) => (dark(p) ? 'rgba(74,205,139,0.14)' : 'rgba(26,159,125,0.10)')}; }
  tr.total td.sticky-col { background: ${(p) => (dark(p) ? '#1f3a2e' : '#dff1ea')}; }
  tr.msg td { padding: 28px 12px; text-align: center; color: ${(p) => p.theme.dim}; }
  .mkt { margin-left: 4px; font-size: 11.5px; font-weight: 700; color: ${(p) => (dark(p) ? '#93c5fd' : '#1d4ed8')}; }
  .mkt.is-row { color: ${(p) => (dark(p) ? '#c4b5fd' : '#6d28d9')}; }

  /* [PHONE-LAYOUT 2026-10-23] phones: a month's name at the start of its five columns (centred over them it sat off the
     screen, so Leads Count showed without its month), and the frozen name column 150 px wide — a long name wraps onto a
     second line (the longest name made it about 190 px, more than half of a phone's table box) */
  @media (max-width: 768px) {
    th.month { text-align: left; }
    thead .sticky-col { white-space: normal; }
    td.name { white-space: normal; width: 150px; min-width: 150px; }
  }
`;

/** [JavaScript.html cellsHtml] 15 cells (5 per month) for one person / subtotal row. [PURE-INK 2026-10-15, user] "no blurry or
 *  light tone of the colour": the script's faded zeros (the `zero` class, 0.45 opacity) are gone — a 0 reads like any figure. */
function Cells({ months }: { months: LgpMonthCell[] }) {
  return (
    <>
      {months.map((m, i) => A_COLS.map((col, c) => {
        const v = m[col.key];
        const cls = ['num', i % 2 ? 'alt' : '', c === 0 ? 'first' : '',
          col.key === 'bank' && v ? 'bank' : '', (col.key === 'sales' || col.key === 'conversion') && v ? 'hit' : ''].filter(Boolean).join(' ');
        return <td key={`${i}-${col.key}`} className={cls}>{col.fmt(v)}</td>;
      }))}
    </>
  );
}

/** [JavaScript.html sumMonths] Subtotals for a filtered set of members (the server's totals cover the full set). */
function sumMonths(members: MonthRow[], n: number): LgpMonthCell[] {
  const out: LgpMonthCell[] = [];
  for (let i = 0; i < n; i++) {
    const acc = { leads: 0, sales: 0, costInr: 0, costUsd: 0, bank: 0, conversion: 0 };
    for (const r of members) {
      const m = r.months[i];
      acc.leads += m.leads; acc.sales += m.sales; acc.costInr += m.costInr; acc.costUsd += m.costUsd; acc.bank += m.bank;
    }
    acc.conversion = acc.leads ? acc.sales / acc.leads : 0;
    out.push(acc);
  }
  return out;
}

/** [LGP-SIDES] One side's people: those tagged with it, each with that side's own figures and only that side's tag. */
function sideRows(rows: MonthRow[], side: 'us' | 'row'): MonthRow[] {
  const tag = SIDE_TAG[side];
  return rows
    .filter((r) => String(r.market || '').split(' + ').includes(tag))
    .map((r) => ({ team: r.team, name: r.name, market: tag, months: r.sideMonths ? r.sideMonths[side] : r.months }));
}

/** One view's table (memoised: the switch only moves the panes, it draws no table again). [JavaScript.html renderAnalysis] */
const MonthTable = React.memo(function MonthTable({ view, analysis, rows, headMonths, search, failure }: {
  view: SideView; analysis: LgpMonthlyReport | null; rows: MonthRow[]; headMonths: { label: string }[]; search: string; failure: string | null;
}) {
  const span = 1 + A_COLS.length * headMonths.length;
  const body = (() => {
    if (!analysis) {
      if (failure) return <tr className="msg"><td colSpan={span}>{failure}</td></tr>;
      return Array.from({ length: 12 }, (_, i) => (
        <tr key={`sk${i}`}>
          <td className="sticky-col"><SkeletonBox $w={`${[70, 55, 80, 62, 74, 58][i % 6]}%`} $h="12px" /></td>
          {Array.from({ length: span - 1 }, (__, c) => <td key={c} className="num"><SkeletonBox $w="38px" $h="12px" $align="right" /></td>)}
        </tr>
      ));
    }
    const q = search.trim().toLowerCase();
    // Overall: by "name (side)" — "row" finds the ROW people. [LGP-SIDES] US / ROW: by name only (the side is the view itself).
    const shown = rows.filter((r) => !q || (view === 'all' ? `${r.name} (${r.market || ''})` : r.name).toLowerCase().includes(q));
    if (!shown.length) {
      const none = view === 'all' ? 'No leads in these months.' : `No active ${SIDE_TAG[view]} people have leads in these months.`;
      return <tr className="msg"><td colSpan={span}>{rows.length ? 'No one matches that filter.' : none}</td></tr>;
    }
    const n = analysis.months.length;
    const out: React.ReactNode[] = [];
    for (const team of analysis.teams) {
      const members = ownerFirst(shown.filter((r) => r.team === team), team);
      if (!members.length) continue;
      out.push(<tr key={`g:${team}`} className="group"><td colSpan={span} className="sticky-col">{team}<span className="count">{` · ${members.length} ${members.length === 1 ? 'person' : 'people'}`}</span></td></tr>);
      for (const r of members) {
        // [LGP-SIDES 2026-10-15, user] "in the us all people is from us only so why the tag … no need for tag": the (US) / (ROW)
        // tag is for Overall, where the two sides are mixed; the US and ROW views show the plain name.
        const mk = view === 'all' ? (r.market || '') : '';
        out.push(
          <tr key={`p:${team}:${r.name}`} data-person={r.name}>
            <td className="sticky-col name">{r.name}{mk && <span className={`mkt${mk === 'ROW' ? ' is-row' : ''}`}>({mk})</span>}</td>
            <Cells months={r.months} />
          </tr>,
        );
      }
      // Overall: the server's team Total (the search's own sum while searching); US / ROW: the side's people of that team.
      const sub = view === 'all' && !q ? analysis.teamTotals[team] : sumMonths(members, n);
      out.push(<tr key={`s:${team}`} className="subtotal"><td className="sticky-col">Total</td><Cells months={sub} /></tr>);
    }
    // The Grand Total closes the table — always the whole set of this view, not the filtered view.
    const grand = view === 'all' ? analysis.totals : sumMonths(rows, n);
    out.push(<tr key="grand" className="total"><td className="sticky-col">Grand Total</td><Cells months={grand} /></tr>);
    return out;
  })();
  return (
    <Table data-monthly-table={view}>
      <thead>
        <tr>
          <th rowSpan={2} className="sticky-col">Leads Generated By</th>
          {headMonths.map((m, i) => <th key={m.label} colSpan={A_COLS.length} className={`month${i % 2 ? ' alt' : ''}`}>{m.label}</th>)}
        </tr>
        <tr>
          {headMonths.map((m, i) => A_COLS.map((c, j) => (
            <th key={`${m.label}-${c.key}`} className={`num${i % 2 ? ' alt' : ''}${j === 0 ? ' first' : ''}`}>{c.label}</th>
          )))}
        </tr>
      </thead>
      <tbody>{body}</tbody>
    </Table>
  );
});

const MonthlyView: React.FC<ViewProps> = ({ active, noDock, storageKey, onVersion, register }) => {
  const nowMonth = todayIst().slice(0, 7); // [LGP-MONTHS] a new month joins the list the day it starts
  const choices = useMemo(() => monthChoices(nowMonth), [nowMonth]);
  const [month, setMonth] = useState(() => defaultRange().month);
  const [analysis, setAnalysis] = useState<LgpMonthlyReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ text: string; error?: boolean } | null>({ text: 'Reading data…' });
  const [search, setSearch] = useState('');
  const [headMonths, setHeadMonths] = useState(() => monthsEndingAt(defaultRange().month));
  // [LGP-SIDES] the switch's choice, remembered in this browser like Leads Count's US | ROW (Overall the first time)
  const sideKey = `dt-lgp-monthly-side:${storageKey}`;
  const [side, setSideState] = useState<SideView>(() => {
    try { const v = localStorage.getItem(sideKey); return v === 'us' || v === 'row' ? v : 'all'; } catch { return 'all'; }
  });
  const req = useRef(0);
  const ref = useRef(analysis);
  ref.current = analysis;
  const busyRef = useRef(busy);
  busyRef.current = busy;

  /** [JavaScript.html loadAnalysis] */
  const load = useCallback((silent: boolean, m?: string) => {
    const want = m ?? (ref.current ? ref.current.months[ref.current.months.length - 1].iso : month);
    if (!silent) {
      setBusy(true);
      setNote({ text: 'Reading data…' });
      setHeadMonths(monthsEndingAt(want));     // the real month columns, before the data lands
      setAnalysis(null);
    }
    const n = ++req.current;
    fetchLgpMonthly(want)
      .then((data) => {
        if (n !== req.current) return;
        setAnalysis(data);
        setHeadMonths(data.months);
        setBusy(false);
        setNote(null);
        onVersion(data.meta.version, 'leads-analysis');
      })
      .catch((err) => {
        if (n !== req.current || silent) return;
        setBusy(false);
        setNote({ text: errorText(err), error: true });
      });
  }, [month, onVersion]);
  const loadRef = useRef(load);
  loadRef.current = load;

  /** [JavaScript.html patchAnalysisPerson] Re-tag the person's (US) / (ROW) at once; who joins or leaves is the re-fetch's job.
   *  [LGP-SIDES] the re-tagged person shows on that side with all their figures until the re-fetch lands. */
  const patchPerson = useCallback((p: PersonPatch) => {
    const cur = ref.current;
    if (!cur || !p || busyRef.current) return;
    if (!/^active$/i.test(String(p.status || '').trim())) return;
    const nm = String(p.name || '').trim().toLowerCase();
    if (!nm) return;
    const label = /^row$/i.test(String(p.rowStatus || '').trim()) ? 'ROW' : 'US';
    let hit = false;
    const rows = cur.rows.map((r) => {
      if (r.name.toLowerCase() === nm && r.market !== label) { hit = true; return { ...r, market: label, sideMonths: undefined }; }
      return r;
    });
    if (hit) setAnalysis({ ...cur, rows });
  }, []);

  const inited = useRef(false);
  useEffect(() => {
    if (!active || inited.current) return;
    inited.current = true;
    loadRef.current(false);
    register('leads-analysis', { refresh: () => loadRef.current(true), patchPerson });
  }, [active, register, patchPerson]);

  /** [LGP-SIDES] Instant — the three tables are already drawn, they slide; remembered per browser. */
  const setSide = (next: SideView) => {
    if (next === side) return;
    setSideState(next);
    try { localStorage.setItem(sideKey, next); } catch { /* storage blocked */ }
  };

  // Each view's people, worked out once per load (not on a switch).
  const rowsByView = useMemo<Record<SideView, MonthRow[]>>(() => {
    const all = analysis ? analysis.rows : [];
    return { all, us: sideRows(all, 'us'), row: sideRows(all, 'row') };
  }, [analysis]);
  const failure = !analysis && note?.error ? note.text : null;

  const shownPeople = rowsByView[side].length;
  const status = note ? note : analysis ? {
    text: `${side === 'all' ? analysis.meta.people : shownPeople} active ${side === 'all' ? '' : `${SIDE_TAG[side]} `}people`
      + ` · USD→INR ${analysis.fx.rate.toFixed(2)}${analysis.fx.stale ? ' (last known rate)' : ''} · data as of ${analysis.meta.dataAsOf}`,
  } : { text: '' };

  return (
    <ViewWrap data-lgp-view="leads-analysis">
      <ViewBar>
        <span className="sub">Active team members only · leads, conversion, cost and revenue · last three months</span>
        <div className="controls">
          <Field data-month=""><span>Up to month</span>
            {/* [LGP-CYCLE 2026-10-27] a picked month loads at once (no Apply) */}
            <MonthSelect liquid value={month} onChange={(e) => { const v = e.target.value; if (v === month) return; setMonth(v); load(false, v); }}>
              {choices.map((c) => <option key={c.iso} value={c.iso}>{c.label}</option>)}
            </MonthSelect>
          </Field>
        </div>
      </ViewBar>
      <Toolbar>
        <div className="left">
          <SearchInput type="search" placeholder="Filter by name" value={search} disabled={busy || !analysis} onChange={(e) => setSearch(e.target.value)} />
          <Seg label="Side" value={side} disabled={busy} onPick={setSide} options={SIDE_OPTIONS} />
        </div>
        <StatusLine $error={!!status.error} data-status="">{status.text}</StatusLine>
      </Toolbar>
      <SideViewport>
        {SIDE_VIEWS.map((v) => (
          <SlidePane key={v} $pos={slidePos(side, v, SIDE_VIEWS)} $quiet $flat $nested data-monthly-pane={v} aria-hidden={side !== v}>
            <TableScroll $noDock={noDock}>
              <MonthTable view={v} analysis={analysis} rows={rowsByView[v]} headMonths={headMonths} search={search} failure={failure} />
            </TableScroll>
          </SlidePane>
        ))}
      </SideViewport>
    </ViewWrap>
  );
};

export default MonthlyView;
